import { Info, Lightbulb, OctagonAlert, TriangleAlert, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import { cn } from '@nucleus-commerce/ui/lib/utils';

export type CalloutType = 'note' | 'tip' | 'warning' | 'danger';

const look: Record<CalloutType, { icon: LucideIcon; className: string }> = {
  note: { icon: Info, className: 'border-info/30 bg-info/5 [&>svg]:text-info' },
  tip: { icon: Lightbulb, className: 'border-success/30 bg-success/5 [&>svg]:text-success' },
  warning: {
    icon: TriangleAlert,
    className:
      'border-warning/50 bg-warning/10 [&>svg]:text-warning-foreground dark:[&>svg]:text-warning',
  },
  danger: {
    icon: OctagonAlert,
    className: 'border-destructive/30 bg-destructive/5 [&>svg]:text-destructive',
  },
};

/** `<Callout type="warning" title="...">`; a markdown blockquote renders as a note. */
export function Callout({
  type = 'note',
  title,
  children,
}: {
  type?: CalloutType;
  title?: string;
  children: ReactNode;
}) {
  const { t } = useTranslation('docs');
  const { icon: Icon, className } = look[type];
  return (
    <Alert role="note" data-callout={type} className={cn('my-6', className)}>
      <Icon aria-hidden />
      <AlertTitle>{title ?? t(`mdx.callout.${type}`)}</AlertTitle>
      <AlertDescription className="text-foreground/85 [&_p]:mt-0 [&_p]:leading-relaxed">
        {children}
      </AlertDescription>
    </Alert>
  );
}
